/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import type { AxiosRequestConfig, AxiosResponse } from 'axios';

import client from 'api/client';
import type { StartAssessmentPayload } from 'api/models/payload/AssessmentStarted';
import type { StartAssessmentResponse } from 'api/models/response/AssessmentStarted';
import type {
    CreateCompletedAssessmentResponse,
    UpdateCompletedAssessmentResponse,
} from 'api/models/response/CompletedAssessment';
import { getActingProfileUserId } from 'utils/selectedAccount';

const candidateInfoUrl = (tableName: string, userId?: number) => {
    const hasUserId = typeof userId === 'number' && Number.isFinite(userId) && userId > 0;
    return hasUserId ? `/candidate_info/${tableName}/${userId}` : `/candidate_info/${tableName}`;
};

export function get(tableName: string, customConfig?: AxiosRequestConfig) {
    return client.get(`/candidate_info/${tableName}`, customConfig);
}

export function getForUser(tableName: string, userId?: number, customConfig?: AxiosRequestConfig) {
    return client.get(candidateInfoUrl(tableName, userId), customConfig);
}

export function post(payload: any, tableName: string, customConfig?: AxiosRequestConfig) {
    const actingId = getActingProfileUserId();
    if (actingId) {
        return client.post(
            `/candidate_info/${tableName}/${actingId}`,
            { data: payload },
            customConfig
        );
    }
    return client.post(`/candidate_info/${tableName}`, { data: payload }, customConfig);
}

export function saveAssessmentStatus(
    userId: string | number
): Promise<AxiosResponse<CreateCompletedAssessmentResponse>> {
    return client.post(`/candidate_info/${userId}/user/completed-assessment`, {});
}

export function saveAssessmentStarted(
    userId: string | number,
    startedAt: string
): Promise<AxiosResponse<StartAssessmentResponse>> {
    const payload: StartAssessmentPayload = { started_at: startedAt };
    return client.post(`/candidate_info/${userId}/user/assessment-started`, payload);
}

export function updateAssessmentCompletionDate(
    userId: string | number
): Promise<AxiosResponse<UpdateCompletedAssessmentResponse>> {
    return client.put(`/candidate_info/${userId}/user/completed-assessment`, {});
}

export function postSales(
    payload: any,
    tableName: string,
    id?: number,
    customConfig?: AxiosRequestConfig
) {
    return client.post(`/candidate_info/${tableName}/${id}`, { data: payload }, customConfig);
}

export function deleteInfo(tableName: string, id?: number) {
    return client.delete(`/candidate_info/${tableName}/${id}`);
}

export const getCandidateInfo = (
    userID: string,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse> => {
    return client.get(`/candidate_info/User/${userID}`, customConfig);
};

// NOTE: these helpers always invoke `set` exactly once — with the payload on
// success, or with `null` on non-2xx / network error. Consumer callbacks rely
// on being called to flip their "loaded" flags; swallowing the error without
// invoking `set` used to leave forms stuck in their loading state forever.

//! Refactoring required! (use get func instead)
export const fetchCandidateInput = async (
    set: CallableFunction,
    tableName: string,
    id?: number,
    options?: { asSelf?: boolean }
) => {
    try {
        const userId = id ?? (options?.asSelf ? undefined : getActingProfileUserId());
        const url = userId
            ? `/candidate_info/${tableName}/${userId}`
            : `/candidate_info/${tableName}`;
        const res = await client.get(url);
        if (res.status !== 200 && res.status !== 201) {
            set(null);
            return false;
        }
        set(res.data ?? null);
        return true;
    } catch (error) {
        console.error(error);
        set(null);
        return false;
    }
};

export const fetchCandidateInputSales = async (
    set: CallableFunction,
    tableName: string,
    id?: number
) => {
    try {
        const url = id ? `/candidate_info/${tableName}/${id}` : `/candidate_info/${tableName}`;
        const res = await client.get(url);
        if (res.status !== 200 && res.status !== 201) {
            set(null);
            return false;
        }
        set(res.data ?? null);
        return true;
    } catch (error) {
        console.error(error);
        set(null);
        return false;
    }
};

//! Refactoring required! (use get func instead)
export const newFetchCandidateInput = async (setData: CallableFunction, category: string) => {
    try {
        const res = await client.get(`/candidate_info/${category}`);
        if (res.status !== 200 && res.status !== 201) {
            setData(null);
            return false;
        }
        if (res.data && (res.data.length || Object.keys(res.data).length)) {
            setData(res.data);
            return true;
        }
        setData(null);
        return false;
    } catch (error) {
        console.error(error);
        setData(null);
        return false;
    }
};

//! Refactoring required!
export const saveProgress = async (tableName: string, data: object) => {
    try {
        const res = await client.post(
            `/candidate_info/${tableName}`,
            { data },
            { headers: { 'Content-Type': 'application/json' } }
        );
        if (res.status !== 200) {
            // alert('Error saving data.')
            return false;
        }
    } catch (error) {
        console.error(error);
    }
};

// In-flight dedupe for GET /forms_filled. Keyed by a constant sentinel since
// the endpoint takes no id. Entry is cleared once the promise settles so we do
// not retain results across mounts (TTL = 0). The swallow-on-error / `false`
// return contract is intentionally preserved — callers rely on it.
const FORMS_FILLED_KEY = 'forms_filled';
const inFlightGetFilledForms = new Map<string, Promise<any>>();

export const getFilledForms = async () => {
    const existing = inFlightGetFilledForms.get(FORMS_FILLED_KEY);
    if (existing) return existing;

    const request = (async () => {
        try {
            const res = await client.get('/forms_filled', {
                headers: { 'Content-Type': 'application/json' },
            });
            if (res.status !== 200) {
                return false;
            }
            if ((res.data && res.data.length) || Object.keys(res.data).length) {
                return res.data;
            }
            return {};
        } catch (error) {
            console.error(error);
            return false;
        }
    })().finally(() => {
        inFlightGetFilledForms.delete(FORMS_FILLED_KEY);
    });

    inFlightGetFilledForms.set(FORMS_FILLED_KEY, request);
    return request;
};

export function postCV(
    file: File,
    candidateId: number | null,
    countryCode?: string | null,
    customConfig?: AxiosRequestConfig
) {
    const formData = new FormData();
    formData.append('file', file);
    if (countryCode) {
        formData.append('country_code', countryCode);
    }

    const url = candidateId
        ? `/candidate_info/upload-cv/${candidateId}`
        : '/candidate_info/upload-cv';
    return client.post(url, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        ...customConfig,
    });
}
